import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './GameStyles.css';

// Утилита для безопасной обработки touch/click на Android WebView
const useTouchClick = (handler: () => void, disabled?: boolean) => {
  const touchedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    touchedRef.current = true;
    // Очищаем предыдущий таймаут
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // ЗАЩИТА: автосброс через 500ms если onTouchEnd не вызвался
    timeoutRef.current = setTimeout(() => {
      touchedRef.current = false;
    }, 500);
  }, [disabled]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (disabled || !touchedRef.current) return;
    e.preventDefault(); // Предотвращаем ghost click
    touchedRef.current = false;
    console.log('🎮 TOUCH END! Calling handler...');
    handler();
    // Сброс через небольшую задержку
    timeoutRef.current = setTimeout(() => {
      touchedRef.current = false;
    }, 300);
  }, [disabled, handler]);

  const onClick = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    // Если это был touch, игнорируем click (ghost click prevention)
    if (touchedRef.current) {
      touchedRef.current = false;
      return;
    }
    console.log('🎮 CLICK! Calling handler...');
    handler();
  }, [disabled, handler]);

  // Обработчик отмены touch (палец ушёл за пределы элемента)
  const onTouchCancel = useCallback(() => {
    touchedRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { onTouchStart, onTouchEnd, onTouchCancel, onClick };
};

interface RocketConfig {
  name: string;
  value: number;
  chance: number;
  duration: number;
  boost_percentage: number;
  style: string;
}

interface MeteorRaceProps {
  onPlay: (choice?: any) => void;
  onClose: () => void;
  cost: number;
  maxReward: number;
  minReward?: number;
  error?: string;
  isPlaying?: boolean;
  config?: RocketConfig[];
  activeBoost?: {
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null;
}

const MeteorRace: React.FC<MeteorRaceProps> = ({
  onPlay,
  onClose,
  cost,
  maxReward,
  error,
  isPlaying,
  config,
  activeBoost
}) => {
  const { t } = useTranslation();
  const [selectedRocket, setSelectedRocket] = useState<number>(1);
  const [showHelp, setShowHelp] = useState(false);

  // Дефолтные ракеты с разными характеристиками
  const defaultRockets: RocketConfig[] = [
    { name: t('games.meteorRace.rockets.redLightning'), value: 1, chance: 0.35, duration: 120, boost_percentage: 50, style: 'safe' },
    { name: t('games.meteorRace.rockets.blueVortex'), value: 2, chance: 0.25, duration: 180, boost_percentage: 100, style: 'balanced' },
    { name: t('games.meteorRace.rockets.cosmicWanderer'), value: 3, chance: 0.20, duration: 240, boost_percentage: 150, style: 'risky' },
    { name: t('games.meteorRace.rockets.stardust'), value: 4, chance: 0.15, duration: 300, boost_percentage: 200, style: 'very_risky' }
  ];

  const rocketsConfig = config && config.length > 0 ? config : defaultRockets;
  const selectedRocketConfig = rocketsConfig.find(r => r.value === selectedRocket) || rocketsConfig[0];

  const handlePlay = useCallback(() => {
    console.log('🎮 MeteorRace handlePlay called! Rocket:', selectedRocket);
    onPlay(selectedRocket);
  }, [onPlay, selectedRocket]);

  // Используем кастомный хук для надёжной работы на Android WebView
  const playBtnEvents = useTouchClick(handlePlay, isPlaying);

  // Визуальные данные ракет
  const rocketVisuals = [
    { id: 1, emoji: '🚀', color: '#22c55e', styleLabel: `🛡️ ${t('games.style.safe')}` },
    { id: 2, emoji: '🛸', color: '#3b82f6', styleLabel: `⚖️ ${t('games.style.balanced')}` },
    { id: 3, emoji: '🛰️', color: '#f59e0b', styleLabel: `⚠️ ${t('games.style.risky')}` },
    { id: 4, emoji: '✨', color: '#ef4444', styleLabel: `🔥 ${t('games.style.veryRisky')}` }
  ];

  return (
    <div className="game-modal">
      <div className="game-content meteor-race">
        <div className="game-header">
          <h2>☄️ {t('games.meteorRace.title')}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="help-btn"
              onClick={() => setShowHelp(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                color: 'white'
              }}
            >
              ❓
            </button>
            <button className="close-btn" onClick={onClose}>✖</button>
          </div>
        </div>

        {/* Индикатор активного буста */}
        {activeBoost && activeBoost.timeLeft > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '8px',
            padding: '10px 14px',
            margin: '10px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
          }}>
            <span style={{ fontSize: '20px' }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', color: 'white', fontSize: '14px' }}>
                {t('games.activeBoost')} x{activeBoost.multiplier}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)' }}>
                {t('games.remaining')}: {Math.floor(activeBoost.timeLeft / 60)}:{(activeBoost.timeLeft % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        )}

        {/* Отображение ошибки */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            margin: '10px 0',
            color: '#ef4444',
            textAlign: 'center'
          }}>
            ❌ {error}
          </div>
        )}

        <div className="race-track">
          {rocketVisuals.map((rocket) => (
            <div
              key={rocket.id}
              className={`race-lane ${isPlaying ? 'racing' : ''}`}
              style={{ '--rocket-color': rocket.color } as React.CSSProperties}
            >
              <div className="rocket-container">
                <span className="rocket-emoji">{rocket.emoji}</span>
              </div>
              <div className="finish-line">🏁</div>
            </div>
          ))}
        </div>

        <div className="rocket-selection">
          <p>{t('games.meteorRace.chooseRocket')}</p>
          <div className="rockets-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {rocketsConfig.map((rocketCfg, index) => {
              const visual = rocketVisuals[index] || rocketVisuals[0];
              return (
                <button
                  key={rocketCfg.value}
                  className={`rocket-btn ${selectedRocket === rocketCfg.value ? 'selected' : ''}`}
                  onClick={() => setSelectedRocket(rocketCfg.value)}
                  disabled={isPlaying}
                  style={{
                    '--rocket-color': visual.color,
                    padding: '10px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  } as React.CSSProperties}
                >
                  <span className="rocket-emoji-big">{visual.emoji}</span>
                  <span className="rocket-name" style={{ fontSize: '11px', fontWeight: 'bold' }}>{rocketCfg.name}</span>
                  <div style={{ fontSize: '9px', opacity: 0.8, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>🎯 {Math.round(rocketCfg.chance * 100)}% {t('games.chance')}</span>
                    <span>⚡ +{rocketCfg.boost_percentage}% {t('games.forTime', { time: Math.round(rocketCfg.duration / 60), unit: t('games.min') })}</span>
                  </div>
                  <span style={{ fontSize: '8px', marginTop: '2px', opacity: 0.7 }}>{visual.styleLabel}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Информация о выбранной ракете */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          padding: '8px 12px',
          margin: '10px 0',
          textAlign: 'center',
          fontSize: '11px'
        }}>
          <strong>{selectedRocketConfig.name}</strong>: {Math.round(selectedRocketConfig.chance * 100)}% {t('games.chance')} → +{selectedRocketConfig.boost_percentage}% {t('games.boost')} {t('games.forTime', { time: Math.round(selectedRocketConfig.duration / 60), unit: t('games.min') })}
        </div>

        <button
          className="play-btn mobile-touch-btn"
          onTouchStart={playBtnEvents.onTouchStart}
          onTouchEnd={playBtnEvents.onTouchEnd}
          onTouchCancel={playBtnEvents.onTouchCancel}
          onClick={playBtnEvents.onClick}
          disabled={isPlaying}
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
        >
          {isPlaying ? `🏁 ${t('games.meteorRace.actions.racing')}` : (error ? `🔄 ${t('games.meteorRace.actions.tryAgain')}` : `🚀 ${t('games.meteorRace.actions.start')}`)}
        </button>

        {/* Модалка помощи */}
        {showHelp && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}
            onClick={() => setShowHelp(false)}
          >
            <div
              style={{
                background: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                padding: '16px',
                maxWidth: '340px',
                width: '100%',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: 'white' }}>📖 {t('games.help.howToPlay')}</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ✖
                </button>
              </div>

              <div style={{ fontSize: '12px', lineHeight: '1.5', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>🎯 {t('games.help.goal')}:</strong><br />
                  {t('games.meteorRace.help.description')}
                </p>

                <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                  <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>🚀 {t('games.meteorRace.help.rocketStats')}:</p>
                  {rocketsConfig.map((r, i) => {
                    const visual = rocketVisuals[i];
                    return (
                      <p key={r.value} style={{ margin: '4px 0', fontSize: '10px' }}>
                        {visual?.emoji} <strong>{r.name}</strong>: {Math.round(r.chance * 100)}% → +{r.boost_percentage}% {t('games.forTime', { time: Math.round(r.duration / 60), unit: t('games.min') })}
                      </p>
                    );
                  })}
                </div>

                <p style={{ marginBottom: '10px' }}>
                  <strong>💡 {t('games.help.strategy')}:</strong><br />
                  🛡️ {t('games.help.safeStrategy')}<br />
                  🔥 {t('games.help.riskyStrategy')}
                </p>

                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  🎮 {t('games.cost')}: <strong>{t('games.free')}</strong><br />
                  🎯 {t('games.attemptsPerDay', { count: 3 })}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeteorRace;